import { useClass } from '@breezerr';
import imgSrc from '@brz-shared/img/src';
import { CircularProgress } from '@mui/material';
import { Config } from '@breezerr';
import React, { ImgHTMLAttributes } from 'react';
import imgAlt from '@brz-shared/img/alt';


type BreakpointKeys = keyof typeof Config.breakpoints;

export type BrzImgDataModel = {
    src: string;
    width: number;
    height: number;
    alt: string;
    aspectRatio: number;
} | null;

export type ImgProps = ImgHTMLAttributes<HTMLImageElement> & {
    width?: number;
    height?: number;
    originalWidth?: number;
    originalHeight?: number;
    mode?: 'fill' | 'thumb' | 'crop' | 'scale' | 'limit' | 'fit';
    quality?: number;
    breakpoints?: {
        [key in BreakpointKeys]?: {
            width?: number;
            height?: number;
        }
    }
    aspectRatio?: number;
    placeholder?: 'box' | 'alt' | 'loading';
    scale?: number;
    gravity?: 'north_east' | 'north' | 'north_west' | 'west' | 'south_west' | 'south' | 'south_east' | 'east' | 'center' | 'face' | 'face:center' | 'faces' | 'faces:center'
    onLoad?: () => void;
};



export const Img = (props: ImgProps) => {
    if (!props.src) return null;

    const { mode, quality, breakpoints, aspectRatio, width, height, originalWidth, originalHeight, placeholder, scale, onLoad, style, ...other } = props;
    
    const [loaded, setLoaded] = React.useState(props.loading === 'eager' ? true : false);
    // const [error, setError] = React.useState(false);
    const imgRef = React.useRef<HTMLImageElement>(null);

    const [widthState, setWidthState] = React.useState<number>();
    const [heightState, setHeightState] = React.useState<number>();
    const [srcState, setSrcState] = React.useState<string>();


    React.useEffect(() => {
        if ( (!!width || !!height) && typeof props.onLoad === 'function' ) {
            props.onLoad();
        }

        if ( typeof window !== 'undefined' && imgRef.current ) {
            imgRef.current.addEventListener('load', handleLoad);

            setSrcState(imgSrc({
                src: props.src || '',
                width: width ? width * pixelRatio : undefined,
                height: height ? height * pixelRatio : undefined,
                mode: props.mode,
                quality: props.quality,
                gravity: props.gravity
            }));

            return () => {
                imgRef.current?.removeEventListener('load', handleLoad);
            }
        }
    }, []);

    const handleLoad = () => {
        if ( !width && !height ) {
            setWidthState(imgRef?.current?.width);
            setHeightState(imgRef?.current?.height);
        }

        if ( typeof props.onLoad === 'function' ) {
            props.onLoad();
        }
        
        setLoaded(true);
    };


    const pixelRatio = typeof window !== 'undefined' ? window.devicePixelRatio : 1;
    
    const src = imgSrc({
        src: props.src || '',
        width: width ? width * pixelRatio : undefined,
        height: height ? height * pixelRatio : undefined,
        mode: props.mode,
        quality: props.quality,
        gravity: props.gravity
    });


    const set = breakpoints ? Object.keys(breakpoints).map((key) => {
        
        const breakpoint = breakpoints[key as BreakpointKeys];
        const definedWidth = (breakpoint?.width || props.width);
        const definedHeight = (breakpoint?.height || props.height);

        if (definedWidth) {
            const size = Config.breakpoints[key as BreakpointKeys].media;
            const w = Math.floor(definedWidth * pixelRatio);

            const s = imgSrc({
                src: props.src || '',
                width: w,
                height: aspectRatio && originalWidth ? aspectRatio * originalWidth : undefined,
                mode: props.mode,
                quality: props.quality,
                gravity: props.gravity
            });

            return {
                srcset: `${s} ${w}w`,
                size: size ? `(max-width: ${size}px) ${Math.floor(w)}px` : `100vw`
            }
        } else if (definedHeight) {
            const size = Config.breakpoints[key as BreakpointKeys].media;
            const h = Math.floor(definedHeight * pixelRatio);
            const w = Math.floor(h * pixelRatio);

            const s = imgSrc({
                src: props.src || '',
                width: aspectRatio && originalHeight ? aspectRatio * originalHeight : undefined,
                height: h,
                mode: props.mode,
                quality: props.quality,
                gravity: props.gravity
            });

            return {
                srcset: `${s} ${w}w`,
                size: size ? `(max-width: ${size}px) ${Math.floor(w)}px` : `100vw`
            }
        } else {
            return {};
        }
    }).filter(item => !!item.srcset) : [];




    const srcset = set.map(item => {
        return item.srcset
    }).join(',') || '';

    const sizes = set.map(item => {
        return item.size
    }).join(',') || '';
    
    // console.log(pixelRatio);
    

    return (
        <img
            {...other}
            ref={imgRef}
            src={!width && !height ? srcState : src}

            alt={props.alt || imgAlt(props.src)}

            srcSet={srcset}
            sizes={sizes}

            width={width ? width * (scale || 1) : (widthState ? widthState * (scale || 1) : (aspectRatio && height ? height * aspectRatio : undefined))}
            height={height ? height * (scale || 1) : (heightState ? heightState * (scale || 1) : (aspectRatio && width ? width * aspectRatio : undefined))}

            loading={props.loading || 'lazy'}
            onError={() => {
                // setError(true);
            }}
            style={{
                // position: !loaded ? 'absolute' : 'relative',
                visibility: !!width || !!height ? undefined : (!loaded ? 'hidden' : undefined),
                aspectRatio: aspectRatio,
                ...style
            }}
        />
    )
};


Img.defaultProps = {
    scale: 1,
    mode: 'limit',
    quality: 85,
    loading: 'lazy'
}